import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from "react";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { useCounterpartyNameMap } from "@components/counterparty/useCounterpartyNameMap";
import { FinancialDocumentPreview } from "@components/documents/FinancialDocumentDetails/FinancialDocumentPreview";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { mapExtractionToAccrualImporterRows } from "@components/accrualImporter/mapExtractionToAccrualImporterRows";
import { AccrualImporterGroupedSectionsView } from "@components/accrualImporter/groupedSections/AccrualImporterGroupedSectionsView";
import {
    getInvoiceGroupSlicesWithWarnings,
    getInvoiceGroupsWarningSummary,
} from "@components/accrualImporter/groupedSections/invoiceGroupWarnings";
import { useAccrualImporterState } from "@components/accrualImporter/useAccrualImporterState";
import { useAccrualDefaultAccountSeeding } from "@components/accrualImporter/useAccrualDefaultAccountSeeding";
import {
    AccrualMode,
    AccrualImporterRow,
} from "@components/accrualImporter/types";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { useDefaultLayout } from "react-resizable-panels";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import { AccrualImporterContext } from "./accrualImporter.context";
import {
    getAllAccrualLineItemExtractions,
    resolveAccrualCounterpartyIdByName,
} from "./accrualImporter.helpers";
import { useAccrualDocumentRowSync } from "./useAccrualDocumentRowSync";
import { AccrualPreviewCommandBar } from "./AccrualPreviewCommandBar";
import styles from "./AccrualDocumentImporter.module.scss";

interface Props {
    financialDocument: FinancialDocumentExtended;
    mode: AccrualDocumentImporterMode;
}

function toAccrualMode(mode: AccrualDocumentImporterMode): AccrualMode {
    return mode === "bills" ? "bill" : "invoice";
}

function patchRowsWithResolvedCounterparties({
    rows,
    extractions,
    counterpartiesMap,
}: {
    rows: AccrualImporterRow[];
    extractions: ReturnType<typeof getAllAccrualLineItemExtractions>;
    counterpartiesMap: Map<string, string>;
}): { rows: AccrualImporterRow[]; didPatch: boolean } {
    const extractionById = new Map(
        extractions.map((extraction) => [extraction.id, extraction]),
    );
    const counterpartyNameByExtractionId = new Map(
        extractions.map((extraction) => [
            extraction.id,
            extraction.counterpartyName,
        ]),
    );
    let didPatch = false;

    const nextRows = rows.map((row) => {
        if (row.counterpartyId) {
            return row;
        }

        const extraction = extractionById.get(row.extractionId);
        if (extraction?.counterpartyId) {
            didPatch = true;
            return { ...row, counterpartyId: extraction.counterpartyId };
        }

        const counterpartyName =
            row.counterpartyName ??
            counterpartyNameByExtractionId.get(row.extractionId) ??
            null;

        if (!counterpartyName) {
            return row;
        }

        const resolvedCounterpartyId = resolveAccrualCounterpartyIdByName({
            counterpartyName,
            counterpartiesMap,
        });

        if (!resolvedCounterpartyId) {
            return row;
        }

        didPatch = true;
        return { ...row, counterpartyId: resolvedCounterpartyId };
    });

    return { rows: nextRows, didPatch };
}

export const AccrualDocumentReviewPanel: React.FC<Props> = ({
    financialDocument,
    mode,
}) => {
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        groupId: "accrual-document-review-panel-group",
        storage: localStorage,
    });
    const { revalidateDocument, setPreviewedRowId } = useContext(
        AccrualImporterContext,
    );
    const { toast } = useToaster();

    const counterpartiesMap = useCounterpartyNameMap();

    const { rows, setRows, addLineItemToGroup, removeRow, addNewInvoice } =
        useAccrualImporterState();
    useAccrualDefaultAccountSeeding({
        rows,
        setRows,
        mode: toAccrualMode(mode),
    });
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const hasInitializedSnapshotRef = useRef(false);
    const initializedDocumentIdRef = useRef<number | null>(null);
    const financialDocumentRef = useRef(financialDocument);
    financialDocumentRef.current = financialDocument;
    const counterpartiesMapRef = useRef(counterpartiesMap);
    counterpartiesMapRef.current = counterpartiesMap;

    const extractionCounterpartyStateKey = useMemo(() => {
        const extractions = getAllAccrualLineItemExtractions(
            financialDocument,
            mode,
        );
        return extractions
            .map(
                (extraction) =>
                    `${extraction.id}:${extraction.counterpartyId ?? ""}`,
            )
            .join("|");
    }, [financialDocument, mode]);

    const handleSyncSuccess = useCallback(() => {
        revalidateDocument({ financialDocumentId: financialDocument.id });
    }, [financialDocument.id, revalidateDocument]);

    const closePreview = useCallback(() => {
        setPreviewedRowId(undefined);
    }, [setPreviewedRowId]);

    const {
        afterChange,
        initializeSnapshot,
        syncServerCounterpartyAssignments,
    } = useAccrualDocumentRowSync({
        financialDocumentId: financialDocument.id,
        mode,
        counterpartiesMap,
        onSyncError: (error) =>
            toast(`Failed to save document: ${error.message}`),
        onSyncWarning: (message) => toast(message),
        onSyncSuccess: handleSyncSuccess,
    });

    useEffect(() => {
        if (initializedDocumentIdRef.current === financialDocument.id) {
            return;
        }

        const seededRows = mapExtractionToAccrualImporterRows({
            financialDocument: financialDocumentRef.current,
            mode,
            counterpartiesMap: counterpartiesMapRef.current,
        });
        const persistedExtractions = getAllAccrualLineItemExtractions(
            financialDocumentRef.current,
            mode,
        );
        const persistedExtractionIds = persistedExtractions.map(
            (extraction) => extraction.id,
        );

        setRows(seededRows);
        initializeSnapshot(
            seededRows,
            persistedExtractionIds,
            persistedExtractions,
        );
        hasInitializedSnapshotRef.current = true;
        initializedDocumentIdRef.current = financialDocument.id;
    }, [financialDocument.id, mode, initializeSnapshot, setRows]);

    useEffect(() => {
        if (initializedDocumentIdRef.current !== financialDocument.id) {
            return;
        }

        const extractions = getAllAccrualLineItemExtractions(
            financialDocumentRef.current,
            mode,
        );
        const hasCounterpartyName = extractions.some((extraction) =>
            Boolean(extraction.counterpartyName),
        );
        const hasServerCounterpartyAssignment = extractions.some((extraction) =>
            Boolean(extraction.counterpartyId),
        );

        if (!hasCounterpartyName && !hasServerCounterpartyAssignment) {
            return;
        }

        const { rows: nextRows, didPatch } =
            patchRowsWithResolvedCounterparties({
                rows: rowsRef.current,
                extractions,
                counterpartiesMap,
            });

        if (!didPatch) {
            return;
        }

        setRows(nextRows);
        syncServerCounterpartyAssignments(extractions, nextRows);
    }, [
        counterpartiesMap,
        extractionCounterpartyStateKey,
        financialDocument.id,
        mode,
        setRows,
        syncServerCounterpartyAssignments,
    ]);

    const afterChangeRef = useRef(afterChange);
    afterChangeRef.current = afterChange;

    useEffect(() => {
        if (!hasInitializedSnapshotRef.current) {
            return;
        }

        const handler = setTimeout(() => {
            afterChangeRef.current(rowsRef.current);
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [rows]);

    useEffect(
        () => () => {
            if (hasInitializedSnapshotRef.current) {
                afterChangeRef.current(rowsRef.current);
            }
        },
        [],
    );

    const accrualMode = toAccrualMode(mode);
    const invoiceGroups = useMemo(
        () => getInvoiceGroupSlicesWithWarnings({ rows, mode: accrualMode }),
        [rows, accrualMode],
    );
    const invoiceGroupsWarningSummary = useMemo(
        () =>
            getInvoiceGroupsWarningSummary({
                groups: invoiceGroups,
                accrualNoun: accrualMode,
            }).summaryLabel,
        [invoiceGroups, accrualMode],
    );

    return (
        <div className={styles.reviewContentContainer}>
            <ResizablePanelGroup
                orientation="horizontal"
                defaultLayout={defaultLayout}
                onLayoutChanged={onLayoutChanged}
            >
                <ResizablePanel
                    defaultSize="50%"
                    minSize="25%"
                    maxSize="75%"
                    id="accrual-document-preview-panel"
                >
                    <div className={styles.previewPanel}>
                        <FinancialDocumentPreview
                            financialDocument={financialDocument}
                            compact
                            withFileName
                        />
                    </div>
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel
                    defaultSize="50%"
                    minSize="25%"
                    maxSize="75%"
                    id="accrual-document-spreadsheet-panel"
                >
                    <div className={styles.rightPanel}>
                        <div className={styles.gridWrapper}>
                            <AccrualImporterGroupedSectionsView
                                mode={accrualMode}
                                groups={invoiceGroups}
                                setRows={setRows}
                                addLineItemToGroup={addLineItemToGroup}
                                removeRow={removeRow}
                                addNewInvoice={addNewInvoice}
                                counterpartiesMap={counterpartiesMap}
                            />
                        </div>
                        <AccrualPreviewCommandBar
                            financialDocument={financialDocument}
                            onClose={closePreview}
                            invoiceGroupsWarningSummary={
                                invoiceGroupsWarningSummary
                            }
                        />
                    </div>
                </ResizablePanel>
            </ResizablePanelGroup>
        </div>
    );
};
